<template>
  <!-- The social lane's sheet (Inbox, Invite, Following, Edit profile).
       Under 700 px: a full-height bottom sheet 64 from the top, a grab
       handle, the caller's header, a scrolling body and a pinned footer
       (the Inbox and Invite boards). From 700 px: a centred card `width`
       wide, `top` from the top (or centred), the same header, body and
       footer. Esc and the scrim close it. -->
  <v-dialog
    v-model="open"
    :max-width="phone ? '100%' : width"
    :persistent="persistent"
    :scrim="true"
    class="social-sheet"
    :class="[phone ? 'social-sheet--phone' : 'social-sheet--wide', !phone && top !== null ? 'social-sheet--at-top' : '']"
    :style="{ '--sheet-top': (phone ? phoneTop : top ?? 0) + 'px' }"
    :content-class="phone ? 'social-sheet-content social-sheet-content--phone' : 'social-sheet-content'"
  >
    <div class="ss-card" role="dialog" :aria-label="label" :class="{ 'is-phone': phone, 'is-top': !phone && top !== null }">
      <div v-if="phone" class="ss-handle" aria-hidden="true" />
      <div class="ss-head"><slot name="header" /></div>
      <div class="ss-body" :class="bodyClass"><slot /></div>
      <div v-if="$slots.footer" class="ss-foot"><slot name="footer" /></div>
    </div>
  </v-dialog>
</template>

<script>
import { viewport, PHONE_MAX } from "@/shell/sizeClass.js";

export default {
  name: "SocialSheet",
  props: {
    modelValue: { type: Boolean, default: false },
    label: { type: String, required: true },
    width: { type: Number, default: 600 },
    // From 700 px: the distance from the top; null centres the card.
    top: { type: Number, default: null },
    phoneTop: { type: Number, default: 64 },
    persistent: { type: Boolean, default: false },
    bodyClass: { type: [String, Array, Object], default: null },
  },
  emits: ["update:modelValue"],
  computed: {
    phone() {
      return viewport.width < PHONE_MAX;
    },
    open: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit("update:modelValue", v);
      },
    },
  },
};
</script>

<style scoped>
.ss-card {
  display: flex;
  flex-direction: column;
  max-height: calc(100dvh - 48px);
  background: var(--cl-sheet);
  border: 1px solid var(--cl-separator);
  border-radius: var(--cl-r-2xl);
  box-shadow: 0 24px 60px var(--cl-scrim);
  color: var(--cl-label);
  font-family: var(--cl-font);
  overflow: hidden;
}
.ss-card.is-top {
  max-height: calc(100dvh - var(--sheet-top, 64px) - 24px);
}
.ss-card.is-phone {
  height: calc(100dvh - var(--sheet-top, 64px));
  max-height: none;
  border: none;
  border-radius: var(--cl-r-sheet) var(--cl-r-sheet) 0 0;
  box-shadow: none;
}
.ss-handle {
  flex-shrink: 0;
  width: 36px;
  height: 5px;
  margin: 6px auto 0;
  border-radius: 3px;
  background: rgba(235, 235, 245, 0.3);
}
.ss-head {
  flex-shrink: 0;
}
.ss-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.ss-foot {
  flex-shrink: 0;
}
</style>

<style>
/* The overlay teleports to <body>, so its placement can't be scoped. */
.v-overlay.social-sheet > .v-overlay__scrim {
  background: var(--cl-scrim) !important;
  opacity: 1 !important;
}
.v-overlay.social-sheet--at-top {
  align-items: flex-start;
}
.v-overlay.social-sheet--at-top > .v-overlay__content {
  margin-top: var(--sheet-top, 64px);
  max-height: calc(100% - var(--sheet-top, 64px) - 24px);
}
.v-overlay.social-sheet--phone {
  align-items: flex-end;
}
.v-overlay.social-sheet--phone > .v-overlay__content.social-sheet-content--phone {
  margin: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  max-height: none !important;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
}
</style>
